/**
 * COPIED/ADAPTED from pages/dev-agent/components/useUpdateMetadataItem.ts.
 *
 * Same hook signature so the copied MetadataEditorPanel logic is unchanged, but
 * the PUT targets the MULTI-AGENT base URL (`/proxy/multi-agent`) instead of the
 * dev-agent (agentic-platform) base. Until that endpoint exists, a 404/405
 * returns a stub so the editor save flow proceeds (content is already in local
 * state and persisted as a branch by sessionSaveWithValidation).
 */

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { MULTI_AGENT_API_BASE_URL, getMultiAgentAuthHeaders } from "@/services/multi-agent";
import { createAxiosInstance } from "@/dataProvider/axios";

interface MetadataItemResult {
  id: string;
  metadataType: string;
  content: string;
  fullName: string;
  fileName: string;
  isEdited: boolean;
  status: string | null;
}

interface UseUpdateMetadataItemOptions {
  onSuccess?: (data: MetadataItemResult) => void;
  onError?: (error: Error) => void;
}

const api = createAxiosInstance(MULTI_AGENT_API_BASE_URL);

async function updateMetadataItem(input: {
  id: string;
  content: string;
}): Promise<MetadataItemResult> {
  try {
    const { data } = await api.put(
      `/api/metadata/items/${encodeURIComponent(input.id)}`,
      { content: input.content },
      { headers: getMultiAgentAuthHeaders() },
    );
    const raw = data?.data ?? data;
    return {
      id: raw.id ?? input.id,
      metadataType: raw.metadata_type ?? "",
      content: raw.content ?? input.content,
      fullName: raw.full_name ?? "",
      fileName: raw.file_name ?? raw.full_name ?? "",
      isEdited: true,
      status: raw.status ?? null,
    };
  } catch (err: any) {
    if (err?.response?.status === 404 || err?.response?.status === 405) {
      console.debug(
        "[multi-agent useUpdateMetadataItem] PUT endpoint not available yet — returning stub response.",
      );
      return {
        id: input.id,
        metadataType: "",
        content: input.content,
        fullName: "",
        fileName: "",
        isEdited: true,
        status: null,
      };
    }
    throw err;
  }
}

export function useUpdateMetadataItem(
  options?: UseUpdateMetadataItemOptions,
): UseMutationResult<MetadataItemResult, Error, { id: string; content: string }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateMetadataItem,
    onSuccess: (data) => {
      if (data?.id) {
        queryClient.invalidateQueries({
          queryKey: ["multiAgent", "metadataItem", data.id],
        });
        queryClient.setQueryData(["multiAgent", "metadataItem", data.id], data);
      }
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}
